// Reusable content fragments for location pages. Each town has its own profile
// (postcodes, landmarks, local-property notes); each service (plumber, handyman)
// has its own shared paragraphs. Mix-and-match keeps every page unique while
// guaranteeing schema/structure consistency.

export const TOWN_PROFILES = {
  rochester: {
    name: 'Rochester',
    slug: 'rochester',
    region: 'Kent',
    postcodes: ['ME1', 'ME2', 'ME3'],
    landmarks: ['Rochester Castle', 'Rochester Cathedral', 'High Street', 'The Esplanade', 'St Margarets', 'Strood'],
    propertyNotes:
      'Rochester has a particularly mixed property stock — Georgian and Victorian terraces near the historic high street with original lead and copper pipework, Edwardian semis through Strood and St Margaret\'s, and post-war estates around Borstal and Wainscott. Older properties commonly need stopcock replacements, lead-pipe upgrades, and modern radiator valves; newer estates need bathroom and kitchen refresh work.',
    typicalIssues:
      'Hard water from the Medway supply means more limescale in cylinders, kettles and shower heads than in softer parts of the South-East. Tap cartridges and shower mixers tend to need replacement sooner here than elsewhere.',
  },
  gillingham: {
    name: 'Gillingham',
    slug: 'gillingham',
    region: 'Kent',
    postcodes: ['ME7', 'ME8'],
    landmarks: ['Gillingham High Street', 'Pier Approach', 'Twydall', 'Rainham', 'Hempstead', 'Grange'],
    propertyNotes:
      'Gillingham has heavy concentrations of 1900s terraces and 1960s-80s semis, plus growing newer developments around Twydall and Hempstead. The terraces typically need bathroom modernisation and selective pipework replacement. The newer-build estates need standard maintenance and the usual silicone, sealant and tap-cartridge upkeep.',
    typicalIssues:
      'Older Gillingham properties often have under-sized cold-water storage tanks in the loft that limit shower performance — many of our jobs here include either tank upgrades or full conversion to unvented mains-fed systems.',
  },
  chatham: {
    name: 'Chatham',
    slug: 'chatham',
    region: 'Kent',
    postcodes: ['ME4', 'ME5'],
    landmarks: ['Chatham Maritime', 'Walderslade', 'Luton', 'Wayfield', 'Princes Park', 'The Pentagon'],
    propertyNotes:
      'Chatham\'s housing ranges from the historic dockyard properties through to extensive post-war housing around Walderslade and the newer-build estates of Chatham Maritime. We see a lot of mid-century pipework reaching end-of-life in the older estates, and bathroom/kitchen refresh work in the newer harbour-side flats.',
    typicalIssues:
      'A lot of Chatham flats have rented-out tenancies which means our work is often landlord-driven: void fix-ups between tenants, urgent repairs during occupancy, and end-of-tenancy snagging.',
  },
  maidstone: {
    name: 'Maidstone',
    slug: 'maidstone',
    region: 'Kent',
    postcodes: ['ME14', 'ME15', 'ME16'],
    landmarks: ['Maidstone Town Centre', 'Allington', 'Bearsted', 'Penenden Heath', 'Loose', 'Tovil'],
    propertyNotes:
      'As the county town, Maidstone has by far the most varied housing in central Kent — period townhouses near the town centre, Victorian villas in Allington and Penenden Heath, traditional semis in Bearsted, and modern apartment developments along Mill Street and St Peter\'s Wharf. Every job is different.',
    typicalIssues:
      'Period property restoration jobs in central Maidstone often involve careful working around heritage features — original lead pipework needs to be replaced sensitively, original floor tiles need protecting during access, and decorative skirtings and architraves need careful refitting.',
  },
};

export const PLUMBER_BODY_INTRO = (town) =>
  `When you search for a "plumber in ${town}", you want someone who actually lives and works in the area — not a national directory that will eventually send a sub-contractor from 40 miles away. H.A.R.P Solutions runs a genuine local engineering operation across ${town} and the wider region, with engineers who know the streets, the housing stock, and the typical issues that come up in this part of Kent.`;

export const HANDYMAN_BODY_INTRO = (town) =>
  `Every property in ${town} accumulates a small backlog of jobs — the door that sticks in winter, the bracket waiting to be wall-mounted, the loose toilet handle, the bathroom silicone that\'s gone mouldy. Booking one of our handymen for an hour or two clears the list quickly. We work across ${town} and surrounding areas with a flexible, hourly or fixed-price model.`;

export const PLUMBER_FAQS = (town) => ([
  {
    q: `How quickly can you get a plumber to ${town}?`,
    a: `For standard plumbing in ${town} we typically book in same-week appointments. For emergency callouts (burst pipes, major leaks, no heat) we target under 90 minutes — engineers based across the region usually have someone within 20-30 minutes of most ${town} postcodes.`
  },
  {
    q: `Do you cover all of ${town} or only specific postcodes?`,
    a: `We cover all of ${town} including outlying villages. Even if you are slightly outside the main town, we will travel — there is rarely an extra charge for being a few miles further out.`
  },
  {
    q: `Are you familiar with the older property stock in ${town}?`,
    a: `Yes. Many of our engineers grew up or have lived locally and have worked on the area\'s Victorian and Edwardian properties for years. We understand the original lead and copper pipework, the typical layouts, and how to modernise sensitively where the property has any heritage value.`
  },
  {
    q: 'Do you provide written quotes?',
    a: 'For anything beyond a quick repair, yes — written quotes are issued before any work begins so you know exactly what you are paying.'
  },
  {
    q: 'Do you work with letting agents and landlords?',
    a: 'Frequently. We have ongoing relationships with several letting agents covering the region. We are happy to invoice landlords or agencies directly, attend with appropriate tenant notice, and provide written job summaries for management records.'
  },
]);

export const HANDYMAN_FAQS = (town) => ([
  {
    q: `What kind of handyman jobs do you do in ${town}?`,
    a: `All the usual: flat-pack assembly, TV wall-mounting, picture hanging, door adjustments, lock changes, silicone resealing, minor carpentry, curtain pole fitting, painting touch-ups, and general property repairs. If it doesn\'t need a specialist trade certificate, we can usually help.`
  },
  {
    q: `Is there a minimum charge for handyman visits in ${town}?`,
    a: 'We work on a one-hour minimum, then in 15-minute increments. There is no separate callout fee on top — what you are quoted is what you pay.'
  },
  {
    q: 'Can you do multiple small jobs in one visit?',
    a: 'Yes — and we encourage it. Bundling several small jobs into one visit is much better value than booking them separately. Send us a list when you call.'
  },
  {
    q: `Are your handymen insured?`,
    a: 'Yes. All H.A.R.P Solutions engineers carry full public liability insurance regardless of whether they are doing handyman or plumbing work.'
  },
  {
    q: `Do you work weekends in ${town}?`,
    a: 'We can attend on Saturdays by arrangement. Sunday work is reserved for genuine emergencies. Weekday evening slots are also sometimes available.'
  },
]);
